// src/components/resources/quizzes/components/QuizCover.tsx

'use client'

import { useState } from 'react'
import { cn } from '~/lib/utils'
import RiSettings4Line from '~/icons/remix/generated/RiSettings4Line'
import RiArrowRightLine from '~/icons/remix/generated/RiArrowRightLine'
import RiInformationLine from '~/icons/remix/generated/RiInformationLine'
import RiTimeLine from '~/icons/remix/generated/RiTimeLine'
import RiListCheck2 from '~/icons/remix/generated/RiListCheck2'
import RiCheckLine from '~/icons/remix/generated/RiCheckLine'
import type { Quiz, FeedbackSettings } from '~/lib/resources/types/tools/quiz'
import { FeedbackTiming, isPlatform } from '~/lib/resources/types/tools/quiz'
import { QuizSettings } from '~/components/resources/quizzes/components/QuizSettings'

interface QuizCoverProps {
  quiz: Quiz
  onStart: () => void
  onOpenSettings: (update: {
    timing?: typeof FeedbackTiming[keyof typeof FeedbackTiming]
    settings?: Partial<FeedbackSettings>
  }) => void
}

export function QuizCover({ quiz, onStart, onOpenSettings }: QuizCoverProps): JSX.Element {
  const [showDetails, setShowDetails] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [feedbackSettings, setFeedbackSettings] = useState<{
    timing: typeof FeedbackTiming[keyof typeof FeedbackTiming]
    settings: FeedbackSettings
  }>(quiz.config.feedbackDefaults ?? {
    timing: FeedbackTiming.IMMEDIATE,
    settings: {
      showVisualIndicators: true,
      showDetailedExplanations: true,
      enableHints: true,
      showSectionSummaries: false
    }
  })
  
  // Calculate total questions and time
  const totalQuestions = quiz.sections.reduce((total, section) => total + section.questions.length, 0)
  const estimatedTime = quiz.estimatedTime ?? '10-15 minutes'

  // Get unique platforms and categories
  const validPlatforms = quiz.sections.flatMap(s => 
    s.questions.map(q => q.platform).filter(isPlatform)
  )
  const validCategories = quiz.sections.flatMap(s => 
    s.questions.map(q => q.category).filter((c): c is string => typeof c === 'string')
  )

  const platforms = new Set(validPlatforms)
  const categories = new Set(validCategories)

  // Get all unique objectives
  const objectives = Array.from(new Set(quiz.sections.flatMap(s => s.objectives)))

  const handleSettingsChange = (update: {
    timing?: typeof FeedbackTiming[keyof typeof FeedbackTiming]
    settings?: Partial<FeedbackSettings>
  }): void => {
    const newSettings = {
      timing: update.timing ?? feedbackSettings.timing,
      settings: {
        ...feedbackSettings.settings,
        ...update.settings
      }
    }
    setFeedbackSettings(newSettings)
    onOpenSettings(update)
  }

  const handleSettingsClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation()
    setShowSettings(!showSettings)
  }

  const handleSettingsPanelClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    e.stopPropagation()
  }

  const handleStartClick = (): void => {
    onStart()
  }

  return (
    <div className="mx-auto max-w-4xl">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white to-gray-50 shadow-xl ring-1 ring-gray-200 dark:from-gray-900 dark:to-gray-800 dark:ring-gray-800">
        {/* Background Pattern */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-grid-gray-900/[0.02] dark:bg-grid-white/[0.02]" />
          <div className="absolute -left-1/4 top-1/4 h-96 w-96 animate-blob rounded-full bg-gradient-to-br from-indigo-100/40 to-blue-100/40 blur-3xl filter dark:from-indigo-900/20 dark:to-blue-900/20" />
          <div className="absolute -right-1/4 top-1/2 h-96 w-96 animate-blob rounded-full bg-gradient-to-br from-blue-100/40 to-indigo-100/40 blur-3xl filter dark:from-blue-900/20 dark:to-indigo-900/20" />
          <div className="absolute left-1/4 top-3/4 h-96 w-96 animate-blob rounded-full bg-gradient-to-br from-indigo-100/40 to-blue-100/40 blur-3xl filter dark:from-indigo-900/20 dark:to-blue-900/20" />
        </div>

        <div className="relative p-4 sm:p-8">
          {/* Settings Button */}
          <div className="absolute right-4 top-4 sm:right-8 sm:top-8">
            <button
              type="button"
              onClick={handleSettingsClick}
              className={cn(
                "flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-lg transition-colors",
                showSettings
                  ? "bg-indigo-100 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400"
                  : "text-gray-500 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300"
              )}
              aria-label="Quiz Settings"
            >
              <RiSettings4Line className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>

            {showSettings && (
              <div 
                data-settings-panel
                className="absolute right-0 top-full z-50 mt-2 w-72 sm:w-80 rounded-lg bg-white p-4 shadow-xl ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/5"
                onClick={handleSettingsPanelClick}
              >
                <QuizSettings
                  feedback={feedbackSettings}
                  onFeedbackChange={handleSettingsChange}
                />
              </div>
            )}
          </div>

          {/* Quiz Header */}
          <div className="text-center">
            <div className="group relative mx-auto inline-flex">
              <div className="absolute -inset-px rounded-2xl bg-gradient-to-r from-indigo-500 to-blue-500 opacity-75 blur transition group-hover:opacity-100" />
              <div className="relative inline-flex h-16 w-16 sm:h-24 sm:w-24 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-50 to-white shadow-lg ring-1 ring-gray-900/5 transition-transform group-hover:scale-105 dark:from-indigo-900/20 dark:to-gray-900 dark:ring-white/5">
                <span className="text-3xl sm:text-4xl transform transition group-hover:scale-110">
                  {quiz.toolCategory === 'platform' ? '🚀' : '📚'}
                </span>
              </div>
            </div>
            {/* Using h1 as this is the main heading for the quiz page.
                The resource layout's showHeader is set to false for quizzes
                to avoid duplicate h1 elements and allow the quiz to control
                its own semantic heading structure. */}
            <h1 className="mt-6 sm:mt-8 text-2xl sm:text-4xl font-bold tracking-tight bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent dark:from-white dark:via-gray-200 dark:to-white">
              {quiz.title}
            </h1>
            <p className="mx-auto mt-3 sm:mt-4 max-w-2xl text-base sm:text-lg text-gray-600 dark:text-gray-400">
              {quiz.description}
            </p>
          </div>

          {/* Quick Stats */}
          <div className="mt-8 sm:mt-12 grid gap-4 sm:gap-6 sm:grid-cols-3">
            <div className="group relative rounded-xl bg-gradient-to-br from-gray-50 to-white p-4 sm:p-6 shadow-sm ring-1 ring-gray-900/5 transition-all hover:shadow-md dark:from-gray-800/50 dark:to-gray-900 dark:ring-white/5">
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-indigo-500/5 to-blue-500/5 opacity-0 transition-opacity group-hover:opacity-100 dark:from-indigo-500/10 dark:to-blue-500/10" />
              <div className="relative flex items-center gap-3 sm:block">
                <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400">
                  <RiListCheck2 className="h-5 w-5 sm:h-6 sm:w-6" />
                </div>
                <div className="sm:mt-4">
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                    {quiz.sections.length}
                  </p>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Sections
                  </p>
                </div>
              </div>
            </div>

            <div className="group relative rounded-xl bg-gradient-to-br from-gray-50 to-white p-4 sm:p-6 shadow-sm ring-1 ring-gray-900/5 transition-all hover:shadow-md dark:from-gray-800/50 dark:to-gray-900 dark:ring-white/5">
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-indigo-500/5 to-blue-500/5 opacity-0 transition-opacity group-hover:opacity-100 dark:from-indigo-500/10 dark:to-blue-500/10" />
              <div className="relative flex items-center gap-3 sm:block">
                <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400">
                  <RiCheckLine className="h-5 w-5 sm:h-6 sm:w-6" />
                </div>
                <div className="sm:mt-4">
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                    {totalQuestions}
                  </p>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Questions
                  </p>
                </div>
              </div>
            </div>

            <div className="group relative rounded-xl bg-gradient-to-br from-gray-50 to-white p-4 sm:p-6 shadow-sm ring-1 ring-gray-900/5 transition-all hover:shadow-md dark:from-gray-800/50 dark:to-gray-900 dark:ring-white/5">
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-indigo-500/5 to-blue-500/5 opacity-0 transition-opacity group-hover:opacity-100 dark:from-indigo-500/10 dark:to-blue-500/10" />
              <div className="relative flex items-center gap-3 sm:block">
                <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400">
                  <RiTimeLine className="h-5 w-5 sm:h-6 sm:w-6" />
                </div>
                <div className="sm:mt-4">
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                    {estimatedTime}
                  </p>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Duration
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Quiz Details */}
          <div className="mt-8 sm:mt-12">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex w-full items-center justify-between rounded-lg bg-white/50 p-3 sm:p-4 text-left shadow-sm ring-1 ring-gray-900/5 transition-colors hover:bg-white/80 dark:bg-gray-800/50 dark:ring-white/5 dark:hover:bg-gray-800/80"
            >
              <span className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
                <RiInformationLine className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-600 dark:text-indigo-400" />
                Quiz Details & Learning Objectives
              </span>
              <RiArrowRightLine 
                className={cn(
                  "h-4 w-4 sm:h-5 sm:w-5 text-gray-400 transition-transform dark:text-gray-500",
                  showDetails && "rotate-90"
                )}
              />
            </button>

            {showDetails && (
              <div className="mt-4 space-y-6 rounded-lg bg-white/50 p-4 sm:p-6 shadow-sm ring-1 ring-gray-900/5 dark:bg-gray-800/50 dark:ring-white/5">
                {/* Learning Objectives */}
                {objectives.length > 0 && (
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                      Learning Objectives
                    </h3>
                    <ul className="mt-4 space-y-3">
                      {objectives.map((objective, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <span className="flex h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400">
                            {index + 1}
                          </span>
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {objective}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Platforms & Categories */}
                {(platforms.size > 0 || categories.size > 0) && (
                  <div className="grid gap-6 sm:grid-cols-2">
                    {platforms.size > 0 && (
                      <div>
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                          Platforms Covered
                        </h3>
                        <ul className="mt-4 space-y-2">
                          {validPlatforms.map((platform) => (
                            <li key={platform} className="flex items-center gap-2">
                              <span className="flex h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-medium text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400">
                                {platform.charAt(0).toUpperCase()}
                              </span>
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                {platform.charAt(0).toUpperCase() + platform.slice(1)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {categories.size > 0 && (
                      <div>
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                          Categories
                        </h3>
                        <ul className="mt-4 space-y-2">
                          {validCategories.map((category) => (
                            <li key={category} className="flex items-center gap-2">
                              <span className="flex h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-medium text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400">
                                {category.charAt(0).toUpperCase()}
                              </span>
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                {category.charAt(0).toUpperCase() + category.slice(1)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Quiz Rules */}
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                    Quiz Rules
                  </h3>
                  <ul className="mt-4 space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="flex h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400">
                        <RiCheckLine className="h-3 w-3 sm:h-4 sm:w-4" />
                      </span>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Passing score: {quiz.config.passingScore ?? 70}%
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400">
                        <RiCheckLine className="h-3 w-3 sm:h-4 sm:w-4" />
                      </span>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {quiz.config.allowRetake ? 'You can retake this quiz multiple times' : 'This quiz can only be taken once'}
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400">
                        <RiCheckLine className="h-3 w-3 sm:h-4 sm:w-4" />
                      </span>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {feedbackSettings.timing === FeedbackTiming.IMMEDIATE
                          ? 'Receive immediate feedback after each question'
                          : feedbackSettings.timing === FeedbackTiming.SECTION
                          ? 'Receive feedback after completing each section'
                          : 'Receive feedback after completing the quiz'}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="mt-8 sm:mt-12 flex flex-col-reverse sm:flex-row items-center gap-4 sm:justify-between">
            <button
              onClick={handleSettingsClick}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-gray-900/10 transition-colors hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:ring-white/10 dark:hover:bg-gray-700"
            >
              <RiSettings4Line className="h-4 w-4" />
              Quiz Settings
            </button>

            <button
              type="button"
              onClick={handleStartClick}
              className="w-full sm:w-auto inline-flex items-center justify-center rounded-lg bg-gradient-to-r from-indigo-600 to-blue-600 px-8 py-3 text-center text-sm font-semibold text-white shadow-sm hover:from-indigo-500 hover:to-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:from-indigo-500 dark:to-blue-500 dark:hover:from-indigo-400 dark:hover:to-blue-400"
            >
              Start Quiz
            </button>
          </div>
        </div>
      </div>
    </div>
  )
} 